<template>
    <div id="quick-replies">
        <manage-quick-reply
            v-if="isManageQuickReplyPanelActive"
            :quick-reply-id="quickReply.id"
            @close-manage-quick-reply-panel="closeManageQuickReplyPanel"
            @create-quick-reply="createQuickReply"
            @update-quick-reply="updateQuickReply"
        />

        <div v-else>
            <h2 class="d-none d-sm-block">
                {{ $t("entities.quick-replies.Select quick reply message") }}
            </h2>
            <p class="d-none d-sm-block">
                {{ $t("entities.quick-replies.description") }}
            </p>

            <b-button variant="primary no-shadow" @click="manageQuickReply(quickReply)">
                {{ $t("entities.quick-replies.Add new quick reply") }}
            </b-button>

            <br /><br />

            <perfect-scrollbar class="scroll-area">
                <b-overlay :show="isLoading">
                    <p v-if="quickReplies.length === 0 && !isLoading">
                        {{ $t("entities.quick-replies.no-quick-replies-found") }}
                    </p>

                    <b-list-group v-else>
                        <b-list-group-item
                            v-for="quickReply in quickReplies"
                            :key="quickReply.id"
                            button
                            class="flex-column align-items-start pb-1 pt-1 border-0"
                            @click="setQuickReply(quickReply)"
                        >
                            <div class="d-flex w-100 justify-content-between round">
                                <h5>
                                    <strong>
                                        <b-badge v-if="quickReply.private" variant="primary mr-25">
                                            {{ $t("Private") }}
                                        </b-badge>
                                        &#47;{{ quickReply.title }}
                                    </strong>
                                </h5>
                                <div class="d-flex justify-content-end align-items-start">
                                    <b-button
                                        v-if="canModify(quickReply, 'update')"
                                        class="btn-icon ml-50"
                                        size="sm"
                                        variant="light"
                                        @click.stop="manageQuickReply(quickReply)"
                                    >
                                        <font-awesome-icon icon="pen" />
                                    </b-button>
                                    <b-button
                                        v-if="canModify(quickReply, 'delete')"
                                        class="btn-icon ml-50"
                                        size="sm"
                                        variant="light"
                                        @click.stop="deleteQuickReply(quickReply)"
                                    >
                                        <font-awesome-icon icon="trash" />
                                    </b-button>
                                </div>
                            </div>
                            <b-card-text class="text-secondary">
                                <span class="quick-reply-content">{{ quickReply.content }}</span>
                            </b-card-text>
                        </b-list-group-item>
                    </b-list-group>
                </b-overlay>
            </perfect-scrollbar>
        </div>
    </div>
</template>

<script>
import { getUserData } from "@/auth/getUserData";
import useActiveChatTemplates from "@/composables/useActiveChatTemplates";
import useTemplates from "@/composables/useTemplates";
import useParseMessage from "@/composables/v2.1/useParseMessage";
import ToastificationContent from "@/layouts/components/toastification/toastificationContent.vue";
import axios from "@/libs/axios";
import i18n from "@/libs/i18n";
import { ref } from "@vue/composition-api";

import { BBadge, BButton, BCardText, BDropdownItemButton, BListGroup, BListGroupItem, BOverlay } from "bootstrap-vue";
import { useToast } from "vue-toastification/composition";
import ManageQuickReply from "./manageQuickReply";

export default {
    components: {
        BButton,
        BDropdownItemButton,
        BBadge,
        BListGroup,
        BListGroupItem,
        BCardText,
        BOverlay,
        ManageQuickReply,
    },
    props: {
        activeChat: {
            type: Object,
            required: true,
        },
    },
    setup(props, context) {
        const toast = useToast();

        const { parseMessage } = useParseMessage();
        const { replaceVariablesByObject, mapVariablesValues, fetchVariablesFromContent } = useActiveChatTemplates(
            props.activeChat
        );

        const isManageQuickReplyPanelActive = ref(false);

        const { quickReplies, fetchQuickReplies, isLoading } = useTemplates();

        fetchQuickReplies({
            params: {
                fields: "title,content,private,userId",
                join: "channels||id",
                filter: `channels.id||$eq||${props.activeChat.channel.id}`,
            },
        });

        const blankQuickReply = {
            channels: [],
            private: 0,
            title: "",
            content: "",
        };
        const quickReply = ref(JSON.parse(JSON.stringify(blankQuickReply)));
        const manageQuickReply = (quickReplyData) => {
            quickReply.value = quickReplyData;
            isManageQuickReplyPanelActive.value = true;
        };
        const closeManageQuickReplyPanel = () => {
            quickReply.value = JSON.parse(JSON.stringify(blankQuickReply));
            isManageQuickReplyPanelActive.value = false;
        };
        const createQuickReply = (quickReply) => {
            axios
                .post(`/quick-replies`, quickReply)
                .then(() => {
                    fetchQuickReplies({
                        params: {
                            fields: "title,content,private,userId",
                            join: "channels||id",
                            filter: `channels.id||$eq||${props.activeChat.channel.id}`,
                        },
                    });
                    closeManageQuickReplyPanel();

                    toast({
                        component: ToastificationContent,
                        props: {
                            title: i18n.t("entities.quick-replies.Quick replies"),
                            text: i18n.t("entities.quick-replies.create-success"),
                            icon: "check",
                            variant: "success",
                        },
                    });
                })
                .catch(() =>
                    toast({
                        component: ToastificationContent,
                        props: {
                            title: i18n.t("entities.quick-replies.Quick replies"),
                            text: i18n.t("entities.quick-replies.create-error"),
                            icon: "times",
                            variant: "danger",
                        },
                    })
                );
        };
        const updateQuickReply = (quickReply) => {
            axios
                .patch(`/quick-replies/${quickReply.id}`, quickReply, { params: { fields: "id" } })
                .then(() => {
                    fetchQuickReplies({
                        params: {
                            fields: "title,content,private,userId",
                            join: "channels||id",
                            filter: `channels.id||$eq||${props.activeChat.channel.id}`,
                        },
                    });
                    closeManageQuickReplyPanel();

                    toast({
                        component: ToastificationContent,
                        props: {
                            title: i18n.t("entities.quick-replies.Quick replies"),
                            text: i18n.t("entities.quick-replies.update-success"),
                            icon: "check",
                            variant: "success",
                        },
                    });
                })
                .catch(() =>
                    toast({
                        component: ToastificationContent,
                        props: {
                            title: i18n.t("entities.quick-replies.Quick replies"),
                            text: i18n.t("entities.quick-replies.update-error"),
                            icon: "times",
                            variant: "danger",
                        },
                    })
                );
        };

        const deleteQuickReply = (quickReply) => {
            context.root.$bvModal
                .msgBoxConfirm(i18n.t("confirm.Are you sure?"), {
                    title: i18n.t("confirm.Are you sure?"),
                    cancelTitle: i18n.t("confirm.Cancel"),
                    cancelVariant: "light",
                    okTitle: i18n.t("confirm.Yes, delete"),
                    hideHeaderClose: false,
                    headerCloseVariant: "light",
                    centered: true,
                })
                .then((result) => {
                    if (result) {
                        return axios
                            .delete(`/quick-replies/${quickReply.id}`)
                            .then(() => {
                                fetchQuickReplies({
                                    params: {
                                        fields: "title,content,private,userId",
                                        join: "channels||id",
                                        filter: `channels.id||$eq||${props.activeChat.channel.id}`,
                                    },
                                });
                                toast({
                                    component: ToastificationContent,
                                    props: {
                                        title: i18n.t("alert.delete.success.title"),
                                        icon: "check",
                                        variant: "success",
                                    },
                                });
                            })
                            .catch(() => {
                                toast({
                                    component: ToastificationContent,
                                    props: {
                                        title: i18n.t("alert.delete.error.title"),
                                        icon: "times",
                                        variant: "danger",
                                    },
                                });
                            });
                    }
                    return false;
                });
        };

        const setQuickReply = async (quickReply) => {
            isLoading.value = true;

            const quickReplyString = await parseMessage({
                inputMessage: quickReply.content,
                parseData: {
                    contact: props.activeChat.contact.id,
                    assignedAgent: props.activeChat.lastConversation.assignedUserId,
                    sendingAgent: getUserData(),
                },
            }).then((result) => result.parsedMessage);

            isLoading.value = false;
            context.emit("set-quick-reply", { content: quickReplyString });
        };

        const canModify = (quickReply, action) => {
            const userData = getUserData();

            const quickReplyAbility = userData.ability.find((ability) => ability.subject === "quickReplies");

            if (quickReplyAbility.possession.includes(`${action}:any`)) {
                return true;
            }

            if (quickReplyAbility.possession.includes(`${action}:own`)) {
                return userData.id === quickReply.userId;
            }

            return false;
        };

        return {
            isLoading,
            isManageQuickReplyPanelActive,
            quickReplies,
            quickReply,

            canModify,
            manageQuickReply,
            closeManageQuickReplyPanel,
            createQuickReply,
            updateQuickReply,
            deleteQuickReply,
            setQuickReply,
            replaceVariablesByObject,
            mapVariablesValues,
            fetchVariablesFromContent,
            getUserData,
        };
    },
};
</script>
